import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from './SEO';
import { LOCAL_SEO_PAGES } from '@/data/localSeoPages';

const siteOrigin =
  process.env.NODE_ENV === 'production' ? 'https://mn-medecine-esthetique.com' : 'http://localhost:3000';

const LocalSeoPage = ({ pageKey }) => {
  const config = LOCAL_SEO_PAGES[pageKey];
  if (!config) {
    return null;
  }

  const v2 =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/v2');
  const pathForCanonical = v2 ? `/v2${config.path}` : config.path;
  const pageUrl = `${siteOrigin}${pathForCanonical}`;

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Accueil',
        item: `${siteOrigin}${v2 ? '/v2/' : '/'}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: config.h1,
        item: pageUrl,
      },
    ],
  };

  return (
    <main className="bg-white min-h-screen pt-28 pb-16 px-6 sm:px-8">
      <SEO
        title={config.title}
        description={config.description}
        keywords={config.keywords}
        url={pathForCanonical}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <article className="max-w-4xl mx-auto text-gray-800">
        <nav className="text-sm text-gray-500 mb-8" aria-label="Fil d’Ariane">
          <Link to="/" className="hover:text-[#1e3a5f]">
            Accueil
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700">{config.h1}</span>
        </nav>

        <h1 className="text-3xl sm:text-4xl font-semibold text-[#1e3a5f] mb-3">{config.h1}</h1>
        <p className="text-lg text-[#1e3a5f]/90 font-medium mb-8">{config.subH1}</p>

        <div className="prose prose-gray max-w-none space-y-4 mb-10">
          {config.intro.map((paragraph, i) => (
            <p key={i} className="text-gray-700 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        {config.citiesList && (
          <section className="mb-10" aria-labelledby="zones-heading">
            <h2 id="zones-heading" className="text-xl font-medium text-gray-900 mb-4">
              Communes et villes desservies
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-700">
              {config.citiesList.map((city) => (
                <li key={city} className="flex items-center">
                  <span className="text-[#1e3a5f] mr-2">•</span>
                  {city}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-gray-600">
              Pages dédiées :{' '}
              <Link to="/medecine-esthetique-lens" className="text-[#1e3a5f] font-medium hover:underline">
                Lens
              </Link>
              {' · '}
              <Link to="/medecine-esthetique-lievin" className="text-[#1e3a5f] font-medium hover:underline">
                Liévin
              </Link>
              {' · '}
              <Link
                to="/medecine-esthetique-henin-beaumont"
                className="text-[#1e3a5f] font-medium hover:underline"
              >
                Hénin-Beaumont
              </Link>
            </p>
          </section>
        )}

        {config.accessNote && (
          <section className="mb-10 p-6 rounded-2xl bg-gray-50 border border-gray-100">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Accès</h2>
            <p className="text-gray-700 text-sm leading-relaxed">{config.accessNote}</p>
          </section>
        )}

        <section className="mb-10 p-6 rounded-2xl bg-[#1e3a5f]/5 border border-[#1e3a5f]/10">
          <h2 className="text-lg font-medium text-[#1e3a5f] mb-3">Adresse du cabinet (NAP)</h2>
          <address className="not-italic text-gray-700 text-sm leading-relaxed">
            <strong>MN Médecine Esthétique</strong>
            <br />
            Maison médicale de Rouvroy
            <br />
            90 rue Louis Daubenton
            <br />
            62320 Rouvroy, France
            <br />
            <a href="tel:+33685951005" className="text-[#1e3a5f] font-medium mt-2 inline-block">
              06 85 95 10 05
            </a>
            <br />
            <a href="mailto:medesthetique@outlook.com" className="text-[#1e3a5f] hover:underline">
              medesthetique@outlook.com
            </a>
          </address>
          <a
            href="https://www.google.com/maps/place/90+Rue+Louis+Daubenton,+62320+Rouvroy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-4 text-sm text-[#1e3a5f] font-medium hover:underline"
          >
            Itinéraire Google Maps →
          </a>
        </section>

        <section className="mb-10" aria-labelledby="soins-heading">
          <h2 id="soins-heading" className="text-xl font-medium text-gray-900 mb-4">
            Soins proposés
          </h2>
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            Découvrez les technologies et tarifs sur la page principale du site.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="/#services"
              className="inline-flex items-center px-4 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#152d4a] transition-colors"
            >
              Voir les soins
            </a>
            <a
              href="/#pricing"
              className="inline-flex items-center px-4 py-2 rounded-full border border-[#1e3a5f] text-[#1e3a5f] text-sm font-medium hover:bg-[#1e3a5f]/5 transition-colors"
            >
              Tarifs
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center px-4 py-2 rounded-full border border-gray-300 text-gray-700 text-sm font-medium hover:border-[#1e3a5f] transition-colors"
            >
              Contact / RDV
            </a>
          </div>
        </section>

        {config.faq && config.faq.length > 0 && (
          <section className="mb-10" aria-labelledby="faq-local-heading">
            <h2 id="faq-local-heading" className="text-xl font-medium text-gray-900 mb-6">
              Questions fréquentes
            </h2>
            <dl className="space-y-6">
              {config.faq.map((item, idx) => (
                <div key={idx} className="border-b border-gray-100 pb-6">
                  <dt className="font-medium text-gray-900 mb-2">{item.q}</dt>
                  <dd className="text-gray-600 text-sm leading-relaxed">{item.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <Link to="/" className="text-[#1e3a5f] font-medium hover:underline">
          ← Retour à l’accueil
        </Link>
      </article>
    </main>
  );
};

export default LocalSeoPage;
